// File: frontend/src/services/websocketService.js

import { API_CONFIG } from '../config/api';
import { WEBSOCKET_RECONNECT } from '../config/constants';

class WebSocketService {
  constructor() {
    this.ws = null;
    this.baseUrl = API_CONFIG.wsUrl;
    this.enterpriseId = API_CONFIG.enterpriseId;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = WEBSOCKET_RECONNECT.MAX_ATTEMPTS;
    this.reconnectDelay = WEBSOCKET_RECONNECT.INITIAL_DELAY;
    this.isIntentionalDisconnect = false;
    this.currentUserId = null;
    this.currentEnterpriseId = null;
    this.currentAutoReconnect = false;
    this.callbacks = {
      onOpen: null,
      onMessage: null,
      onClose: null,
      onError: null
    };
  }

  connect(userId, enterpriseId, onOpen, onMessage, onClose, onError, autoReconnect = true) {
    if (this.ws?.readyState === WebSocket.OPEN) return;

    this.currentUserId = userId;
    this.currentEnterpriseId = enterpriseId || this.enterpriseId;
    this.currentAutoReconnect = autoReconnect;
    this.isIntentionalDisconnect = false;
    this.callbacks = { onOpen, onMessage, onClose, onError };

    const wsUrl = `${this.baseUrl}?user_id=${userId}&enterprise_id=${this.currentEnterpriseId}`;

    try {
      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = (event) => {
        this.reconnectAttempts = 0;
        if (this.callbacks.onOpen) this.callbacks.onOpen(event);
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (this.callbacks.onMessage) this.callbacks.onMessage(data);
        } catch (error) {
          console.error('[WS] Message parse error:', error);
          if (this.callbacks.onError) this.callbacks.onError(error);
        }
      };

      this.ws.onclose = (event) => {
        if (this.callbacks.onClose) this.callbacks.onClose(event);

        const isGoingAway = event.code === 1001;

        if (!this.isIntentionalDisconnect &&
            !isGoingAway &&
            this.currentAutoReconnect &&
            this.reconnectAttempts < this.maxReconnectAttempts) {

          this.reconnectAttempts++;
          const delay = Math.min(
            this.reconnectDelay * this.reconnectAttempts,
            WEBSOCKET_RECONNECT.MAX_DELAY
          );

          setTimeout(() => {
            this.connect(
              this.currentUserId,
              this.currentEnterpriseId,
              this.callbacks.onOpen,
              this.callbacks.onMessage,
              this.callbacks.onClose,
              this.callbacks.onError,
              this.currentAutoReconnect
            );
          }, delay);
        } else {
          this.cleanup();
        }
      };

      this.ws.onerror = (error) => {
        console.error('[WS] Error:', error);
        if (this.callbacks.onError) this.callbacks.onError(error);
      };

    } catch (error) {
      console.error('[WS] Connection error:', error);
      if (this.callbacks.onError) this.callbacks.onError(error);
    }
  }

  executeCommand(command, data, userId, enterpriseId) {
    const message = {
      action: 'command',
      command,
      data: {
        ...data,
        requesting_user_id: userId,
        enterprise_id: enterpriseId || this.enterpriseId
      }
    };
    this.send(message);
  }

  send(message) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    } else {
      console.error('[WS] Send failed — not connected');
      if (this.callbacks.onError) {
        this.callbacks.onError(new Error('WebSocket not connected'));
      }
    }
  }

  disconnect() {
    this.isIntentionalDisconnect = true;
    if (this.ws) {
      this.ws.close(1000, 'Client disconnect');
      this.ws = null;
    }
    this.cleanup();
  }

  cleanup() {
    this.reconnectAttempts = 0;
    this.currentUserId = null;
    this.currentEnterpriseId = null;
    this.currentAutoReconnect = false;
    this.callbacks = {
      onOpen: null,
      onMessage: null,
      onClose: null,
      onError: null
    };
  }

  isConnected() {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  getState() {
    if (!this.ws) return 'DISCONNECTED';
    switch (this.ws.readyState) {
      case WebSocket.CONNECTING: return 'CONNECTING';
      case WebSocket.OPEN:       return 'OPEN';
      case WebSocket.CLOSING:    return 'CLOSING';
      case WebSocket.CLOSED:     return 'CLOSED';
      default:                   return 'UNKNOWN';
    }
  }
}

const webSocketService = new WebSocketService();
export default webSocketService;